import { Box, TextFieldProps, useTheme } from '@mui/material';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

export type HtmlInputProps = Omit<TextFieldProps, 'variant' | 'error' | 'value' | 'onChange'> & {
  error?: string;
  value?: string;
  help?: string;
  icon?: React.ReactNode;
  onClear?: () => void;
  readonly?: boolean;
  onChange: (value: string) => void;
};

function convertFromHtml(text: string) {
  const blocksFromHtml = htmlToDraft(text);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

  return EditorState.createWithContent(contentState);
}

function convertToHtml(editorState: EditorState) {
  return draftToHtml(convertToRaw(editorState.getCurrentContent()));
}

export const HtmlEditor = (p: HtmlInputProps) => {
  const { value, onChange, onBlur, name } = p;
  const { $t } = useIntl();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [editorState, setEditorState] = useState(
    value ? convertFromHtml(value) : EditorState.createEmpty(),
  );

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '6px',
        '& .editor': {
          px: 2,
          py: 1,
          color: 'text.primary',
        },
        '& .public-DraftStyleDefault-block': {
          my: 1,
        },
        '& .rdw-editor-toolbar': {
          background: 'transparent',
          border: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
          marginBottom: 0,
        },
        // Dark mode overrides for react-draft-wysiwyg toolbar
        ...(isDark && {
          '& .rdw-option-wrapper': {
            background: 'transparent',
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            '&:hover': {
              background: theme.palette.action.hover,
              boxShadow: 'none',
            },
          },
          '& .rdw-option-active': {
            background: theme.palette.action.selected,
            boxShadow: 'none',
          },
          '& .rdw-dropdown-wrapper': {
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            '&:hover': {
              background: theme.palette.action.hover,
              boxShadow: 'none',
            },
          },
          '& .rdw-dropdown-selectedtext': {
            color: theme.palette.text.primary,
          },
          '& .rdw-dropdown-carettoopen, & .rdw-dropdown-carettoclose': {
            borderTopColor: theme.palette.text.secondary,
            borderBottomColor: theme.palette.text.secondary,
          },
          '& .rdw-dropdownoption-default': {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '&:hover': {
              background: theme.palette.action.hover,
            },
          },
          '& .rdw-dropdownoption-active, & .rdw-dropdownoption-highlighted': {
            background: theme.palette.action.selected,
          },
          '& .rdw-dropdown-optionwrapper': {
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          },
        }),
        a: {
          color: 'text.primary',
        },
      }}
    >
      <Editor
        editorState={editorState}
        editorClassName="editor"
        stripPastedStyles
        onEditorStateChange={(newState) => {
          setEditorState(newState);
          onChange?.(convertToHtml(newState));
        }}
        onBlur={() => {
          onBlur?.({
            target: { name: name! },
          } as React.FocusEvent<HTMLInputElement>);
        }}
        toolbar={{
          options: ['fontSize', 'inline', 'blockType', 'textAlign', 'history'],
          inline: {
            options: ['bold', 'italic', 'underline', 'strikethrough'],
          },
        }}
        placeholder={$t({
          id: 'common.insertContentHere',
          defaultMessage: 'Insert content here',
        })}
      />
    </Box>
  );
};
